import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Auth } from 'aws-amplify';
import './EmailAlerts.css';

const EmailAlerts = ({ user }) => {
    const [metrics, setMetrics] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [dateRange, setDateRange] = useState({
        start: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        end: new Date().toISOString().split('T')[0]
    });
    const [activeTab, setActiveTab] = useState('summary');

    const API_ENDPOINT = 'https://v6d18bgl57.execute-api.us-west-1.amazonaws.com/prod/admin-get-ses';

    useEffect(() => {
        fetchMetrics();
    }, [dateRange]);

    const fetchMetrics = async () => {
        setLoading(true);
        try {
            const session = await Auth.currentSession();
            const token = session.getIdToken().getJwtToken();
            const response = await axios.get(API_ENDPOINT, {
                params: {
                    action: 'metrics',
                    startDate: dateRange.start + 'T00:00:00Z',
                    endDate: dateRange.end + 'T23:59:59Z'
                },
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            setMetrics(response.data);
            setError(null);
        } catch (err) {
            console.error('Error fetching email metrics:', err);
            setError('Failed to load email metrics');
        } finally {
            setLoading(false);
        }
    };

    const handleDateChange = (e) => {
        const { name, value } = e.target;
        setDateRange(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const renderSummaryCards = () => {
        if (!metrics?.summary) return null;

        const cards = [
            { title: 'Emails Sent', value: metrics.summary.send || 0, color: '#4CAF50' },
            { title: 'Delivered', value: metrics.summary.delivery || 0, color: '#2196F3' },
            { title: 'Delivery Rate', value: `${metrics.summary.deliveryRate || 0}%`, color: '#00BCD4' },
            { title: 'Opens', value: metrics.summary.open || 0, color: '#9C27B0' },
            { title: 'Open Rate', value: `${metrics.summary.openRate || 0}%`, color: '#673AB7' },
            { title: 'Clicks', value: metrics.summary.click || 0, color: '#FF9800' },
            { title: 'Bounces', value: metrics.summary.bounce || 0, color: '#F44336' },
            { title: 'Complaints', value: metrics.summary.complaint || 0, color: '#E91E63' },
        ];

        return (
            <div className="summary-cards">
                {cards.map((card, index) => (
                    <div key={index} className="metric-card" style={{ borderTop: `4px solid ${card.color}` }}>
                        <h3>{card.title}</h3>
                        <p className="metric-value">{card.value}</p>
                    </div>
                ))}
            </div>
        );
    };

    const renderBounces = () => {
        if (!metrics?.bounces) return null;

        return (
            <div className="bounces-section">
                <h3>Bounce Analysis</h3>
                <div className="bounce-summary">
                    <p>Total Bounces: <strong>{metrics.bounces.total}</strong></p>
                    {Object.entries(metrics.bounces.byType).map(([type, data]) => (
                        <div key={type} className="bounce-type">
                            <h4>{type} Bounces: {data.count}</h4>
                            <ul>
                                {data.recipients.slice(0, 5).map((r, i) => (
                                    <li key={i}>
                                        {r.email} - {new Date(r.timestamp).toLocaleString()}
                                        {r.subType && ` (${r.subType})`}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    const renderRecentEvents = () => {
        if (!metrics?.recentEvents) return null;

        return (
            <div className="recent-events">
                <h3>Recent Email Events</h3>
                <table className="events-table">
                    <thead>
                        <tr>
                            <th>Time</th>
                            <th>Event</th>
                            <th>Recipient</th>
                            <th>Template</th>
                            <th>Details</th>
                        </tr>
                    </thead>
                    <tbody>
                        {metrics.recentEvents.map((event, index) => (
                            <tr key={index} className={`event-${event.eventType.toLowerCase()}`}>
                                <td>{new Date(event.timestamp).toLocaleString()}</td>
                                <td>
                                    <span className={`event-badge ${event.eventType.toLowerCase()}`}>
                                        {event.eventType}
                                    </span>
                                </td>
                                <td>{event.recipient}</td>
                                <td>{event.templateName}</td>
                                <td>
                                    {event.eventType === 'Bounce' && event.bounceType}
                                    {event.eventType === 'Click' && event.link}
                                    {event.eventType === 'Delivery' && '✓ Delivered'}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    };

    if (loading) {
        return <div className="loading">Loading email metrics...</div>;
    }

    if (error) {
        return <div className="error">{error}</div>;
    }

    return (
        <div className="email-alerts-container">
            <div className="header">
                <h2>Email Analytics</h2>
                <div className="date-filters">
                    <label>
                        Start Date:
                        <input
                            type="date"
                            name="start"
                            value={dateRange.start}
                            onChange={handleDateChange}
                        />
                    </label>
                    <label>
                        End Date:
                        <input
                            type="date"
                            name="end"
                            value={dateRange.end}
                            onChange={handleDateChange}
                        />
                    </label>
                    <button onClick={fetchMetrics}>Refresh</button>
                </div>
            </div>

            <div className="tabs">
                <button
                    className={activeTab === 'summary' ? 'active' : ''}
                    onClick={() => setActiveTab('summary')}
                >
                    Summary
                </button>
                <button
                    className={activeTab === 'bounces' ? 'active' : ''}
                    onClick={() => setActiveTab('bounces')}
                >
                    Bounces
                </button>
                <button
                    className={activeTab === 'events' ? 'active' : ''}
                    onClick={() => setActiveTab('events')}
                >
                    Recent Events
                </button>
            </div>

            <div className="tab-content">
                {activeTab === 'summary' && renderSummaryCards()}
                {activeTab === 'bounces' && renderBounces()}
                {activeTab === 'events' && renderRecentEvents()}
            </div>
        </div>
    );
};

export default EmailAlerts;
